import GameObject from '../../../GameObject/GameObject';
import AnimationClip from '../AnimationClip';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import Renderable from '../../../Component/Renderable';
import TransformComponent from '../../../Component/Transform';
import { Easing } from '@tweenjs/tween.js';
import { AwaitIterators } from '../AnimationHelper';
import TornadoCard from '../../../Prototype/Level/Card/TornadoCard';
import BaseCardHandler from '../../Card/Handler/BaseCardHandler';
import CardSystem, { getCardHandler } from '../../Card/CardSystem';
import SpriteComponent from '../../../Component/Render/Sprite';
import { MathHelper } from '../../../Utility/MathHelper';
import Scene from '../../../Scene/Scene';
import AnimationFrame from '../../../Component/AnimationFrame';
import {
    createPositionTween,
    createAlphaTween,
    getTweenProps,
} from '../AnimationTweenHelper';
import Point from '../../../Utility/Point';
import Connected from 'Prototype/Level/Card/Modifier/Connected';

interface ICardTornadoMeta {
    scene: Scene;
    sourceCard: TornadoCard;
    targets: any[];
    cardScore: number;
    system: CardSystem;
}

export class CardTornado extends AnimationClip {
    public *execute(
        animator: GameObject,
        meta: ICardTornadoMeta
    ): IterableIterator<number> {
        animator.addChild(meta.sourceCard);

        const tornados = [];
        for (let target of meta.targets) {
            animator.addChild(target.card);
            const connectedModifier = target.card.getModifier(Connected);

            if (connectedModifier) {
                animator.addChild(connectedModifier.rope);
            }

            // Add as child to animator to bring in front of overlay.
            tornados.push(
                this.tornadoSpin(
                    animator,
                    target.card,
                    meta.sourceCard,
                    target.oldValue,
                    target.newValue
                )
            );
        }

        meta.scene.emit('UpdateScoreProgress');

        // Fade out source card.
        const sourceCardAlphaTween = createAlphaTween({
            ...getTweenProps(meta.sourceCard, this, { alpha: 0 }),
            duration: 200,
        });

        yield* AwaitIterators([
            this.startTween(sourceCardAlphaTween),
            ...tornados,
        ]);

        meta.system.sortCardsInPlay(meta.system.store.getState().levelState.cardsState);
    }

    private *tornadoSpin(
        animator: GameObject,
        targetCard: BaseCard,
        sourceCard: TornadoCard,
        oldValue: number,
        newValue: number
    ): IterableIterator<number> {
        const targetCardTransform = targetCard.getComponent(TransformComponent);

        // Create tornado.
        const tornado = GameObject.Instantiate(AnimationFrame, {
            resourceName: 'CardsVFX',
            animationName: 'tornado',
            frameRate: 1000 / 80,
            autoStart: true,
            anchor: Point.Center,
        });
        tornado.addComponent(TransformComponent);
        tornado.addComponent(Renderable);

        try {
            sourceCard.addChild(tornado);
            animator.addChild(tornado);

            // Tween tornado from source card position to target card position.
            const tornadoPositionTween = createPositionTween({
                targetObject: tornado,
                animationClip: this,
                easing: Easing.Quadratic.InOut,
                meta: new Point(
                    targetCardTransform.position.x,
                    targetCardTransform.position.y
                ),
                duration: 800,
            });

            yield* this.startTween(tornadoPositionTween);

            // Tornado grow / fade out.
            const tornadoAlphaTween = this.getTween(
                tornado.getComponent(Renderable)
            );
            tornadoAlphaTween.to({ alpha: 0 }, 400).start(this.elapsedTime);
            const tornadoScaleTween = this.getTween(
                tornado.getComponent(TransformComponent).scale
            );
            tornadoScaleTween
                .to({ x: 2, y: 2 }, 400)
                .easing(Easing.Quadratic.In)
                .start(this.elapsedTime);

            //

            const cardSpinElapsedTimeTween = this.getTween({
                progress: 0,
            });

            cardSpinElapsedTimeTween
                .to({ progress: 1 }, 1500)
                .easing(Easing.Quadratic.Out)
                .onUpdate(o => {
                    this.setCardFlipRotation(targetCard, o.progress, 13);

                    // Swap textures at 80% -- looks fine.
                    if (o.progress > 0.8) {
                        targetCard
                            .getChildByName('cardFrontSprite')
                            .getComponent(
                                SpriteComponent
                            ).texture = `card${newValue + 1}`;
                    }
                });

            yield* this.startTween(cardSpinElapsedTimeTween);

            // Destroy tornado gameobject.
            tornado.destroy();
        } catch (e) {
            console.log(e);
        }
    }

    /**
     * Set the flip rotation given a percentage of time.
     * @param card
     * @param progress
     * @param spins
     */
    private setCardFlipRotation(
        card: BaseCard,
        progress: number,
        spins: number
    ) {
        const transform: TransformComponent = card.getComponent(
            TransformComponent
        );

        const spinDuration = 1 / spins;
        const halfSpinDuration = spinDuration / 2;

        //transform.scale.x =
        //    (Math.cos((progress * Math.PI) / halfSpinDuration) + 1) / 2;

        transform.scale.x =
            MathHelper.pingPong(progress + halfSpinDuration, halfSpinDuration) /
            halfSpinDuration;

        const faceUp =
            Math.floor((progress - halfSpinDuration) / spinDuration) % 2 === 0;

        const handler: BaseCardHandler = getCardHandler(card);
        handler.setCardFaceUp(faceUp);
    }
}
